import SchedulingFilterTypeSettings from '~/components/forms/edit/components/SchedulingFilterTypeSettings';
import React from 'react';
import HidePriceDurationSetting from '~/components/forms/edit/components/HidePriceDurationSetting';
import HideSpotsLeftSetting from '~/components/forms/edit/components/HideSpotsLeftSetting';
import OptionToggle from '~/components/OptionToggle';
import QuantityOptionSettings from '~/components/forms/edit/components/QuantityOptionSettings';
import QuantityLabelSetting from '~/components/forms/edit/components/QuantityLabelSetting';
import DisableWaitlistSetting from '~/components/forms/edit/components/DisableWaitlistSetting';
import TextInput from '~/components/TextInput';
import IconSelectButton from '~/components/IconSelectButton';
import {
    CalendarIcon,
    ListBulletIcon,
    Squares2X2Icon,
} from '@heroicons/react/24/outline';
import WeeklyIcon from '~/components/icons/WeeklyIcon.jsx';
import MonthlyIcon from '~/components/icons/MonthlyIcon.jsx';
import InfoMessageBlock from '~/components/InfoMessageBlock.jsx';
import { useEventTypesQuery } from '~/lib/Queries';
import { Link } from 'react-router-dom';
import { getBusinessUrl } from '~/lib/useBusinessNavigate';
import WarningMessageBlock from '~/components/WarningMessageBlock.jsx';
import ClassListViewIcon from '~/components/icons/ClassListViewIcon.jsx';

const EditClassSchedulingForm = ({ settings, setSettings }) => {
    const view = settings?.view || 'weekly';
    const { data: eventTypes } = useEventTypesQuery();

    // Filter to only class event types
    const classEventTypes = (eventTypes || []).filter(type => type?.is_class);
    const hasNoClassEventTypes = classEventTypes.length === 0 && eventTypes?.length > 0;

    return (
        <div>
            <div className="panel-label mt-4">View</div>
            <div className="grid grid-cols-2 gap-2">
                <IconSelectButton
                    label="Weekly"
                    onClick={() => setSettings({ ...settings, view: 'weekly' })}
                    isSelected={view === 'weekly'}
                    icon={<WeeklyIcon className="w-8 h-8" />}
                    className=" bg-white text-left"
                />
                <IconSelectButton
                    className="text-left bg-white"
                    label="Monthly"
                    onClick={() =>
                        setSettings({ ...settings, view: 'monthly' })
                    }
                    isSelected={view === 'monthly'}
                    icon={<MonthlyIcon className="w-8 h-8" />}
                />
                <IconSelectButton
                    className="text-left bg-white"
                    label="List"
                    onClick={() => setSettings({ ...settings, view: 'list' })}
                    isSelected={view === 'list'}
                    icon={<ClassListViewIcon />}
                />
                <IconSelectButton
                    className="text-left bg-white"
                    label="Card"
                    onClick={() => setSettings({ ...settings, view: 'card' })}
                    isSelected={view === 'card'}
                    icon={<Squares2X2Icon className="w-8 h-8" />}
                />
            </div>

            <SchedulingFilterTypeSettings
                settings={settings}
                setSettings={setSettings}
                onlyClasses={true}
            />

            {hasNoClassEventTypes && (
                    <WarningMessageBlock showIcon={true} className="mt-4">
                        There are no class event types set up yet.<br /><br />Please set up a
                        class event type under{' '}
                        <Link
                            to={getBusinessUrl('/scheduling/event-types')}
                            className="underline"
                        >
                            Event Types →
                        </Link>
                    </WarningMessageBlock>
            )}

            <div className="panel-label mt-4">Options</div>
            <div className="panel p-4">
                <HidePriceDurationSetting
                    settings={settings}
                    setSettings={setSettings}
                />
                <HideSpotsLeftSetting
                    settings={settings}
                    setSettings={setSettings}
                    className="mt-4"
                />
                <OptionToggle
                    value={settings?.hideBookableResource}
                    onChange={value =>
                        setSettings({
                            ...settings,
                            hideBookableResource: value,
                        })
                    }
                    title="Hide Person/Resource Name"
                    description="Don't show who the class is booked with"
                    className="mt-4"
                />
                <OptionToggle
                    value={settings?.hideFullClasses}
                    onChange={value =>
                        setSettings({
                            ...settings,
                            hideFullClasses: value,
                        })
                    }
                    title="Hide Full Classes"
                    description="Don't show classes when there are no spots remaining"
                    className="mt-4"
                />
                <OptionToggle
                    value={settings?.enableRecurring}
                    onChange={value =>
                        setSettings({
                            ...settings,
                            enableRecurring: value,
                        })
                    }
                    title="Allow Booking Multiple"
                    description="Allow clients to select multiple classes to book at one time"
                    className="mt-4"
                />

                <QuantityOptionSettings
                    setSettings={setSettings}
                    settings={settings}
                />

                <DisableWaitlistSetting
                    setSettings={setSettings}
                    settings={settings}
                    className="mt-4"
                />
            </div>

            <TextInput
                value={settings?.bookLabel || ''}
                onChange={value =>
                    setSettings({
                        ...settings,
                        bookLabel: value,
                    })
                }
                label="Booking Button Label"
                placeholder="Book"
                className="mt-4"
            />

            <QuantityLabelSetting
                settings={settings}
                setSettings={setSettings}
            />
        </div>
    );
};

export default EditClassSchedulingForm;
